import React, { useRef, useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { HiOutlineExternalLink, HiOutlineFire, HiOutlineChevronUp, HiOutlineChevronDown } from 'react-icons/hi';
import TokenStack from './TokenStack';
import ChainIcon from './ChainIcon';
import ProtocolIcon from './ProtocolIcon';
import { PROTOCOL_COLORS, CHAIN_COLORS, RISK_MAP } from '../data';
import { fmt } from '../utils';
import { useTheme } from '../theme';
import { InfoIcon } from './Tooltip';
import TOOLTIP_CONTENT from '../tooltipContent';
import { useIsMobile, useIsTablet } from '../useMediaQuery';

// Single shared observer for all rows
const revealCallbacks = new Map();
let sharedObserver = null;

function getSharedObserver() {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const cb = revealCallbacks.get(entry.target);
            if (cb) { cb(); revealCallbacks.delete(entry.target); }
            sharedObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );
  }
  return sharedObserver;
}

function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = getSharedObserver();
    revealCallbacks.set(el, () => setVisible(true));
    obs.observe(el);
    return () => { obs.unobserve(el); revealCallbacks.delete(el); };
  }, []);

  return { ref, visible };
}

const HEADER_SORT_MAP = {
  'APR': 'apr',
  'TVL': 'tvl',
  'VOL 24H': 'volume24h',
  'FEES 24H': 'fees24h',
  'RISK': 'risk',
};

const HEADER_TOOLTIPS = {
  'APR': TOOLTIP_CONTENT.APR,
  'TVL': TOOLTIP_CONTENT.TVL,
  'VOL 24H': TOOLTIP_CONTENT['VOL 24H'],
  'FEES 24H': TOOLTIP_CONTENT['FEES 24H'],
  'RISK': TOOLTIP_CONTENT.RISK,
};

const RISK_TOOLTIP_TEXT = {
  LOW: "LOW risk: Both tokens are recognized stablecoins. Minimal impermanent loss expected.",
  MED: "MEDIUM risk: One volatile token (ETH, WBTC) paired with a stablecoin. Moderate impermanent loss possible.",
  HIGH: "HIGH risk: Both tokens are volatile or have low liquidity. Significant impermanent loss likely.",
};

const PROTOCOL_TOOLTIP_TEXT = {
  "Curve": "Curve Finance — optimized for stablecoin and pegged-asset swaps with low slippage.",
  "Uniswap V3": "Uniswap V3 — concentrated liquidity AMM for capital-efficient trading.",
  "Balancer": "Balancer — flexible multi-token pools with customizable weights.",
  "PancakeSwap": "PancakeSwap — leading DEX on BNB Chain with farms and syrup pools.",
  "Raydium": "Raydium — hybrid AMM on Solana with order book integration.",
  "Orca": "Orca — user-friendly DEX on Solana with concentrated liquidity.",
  "Kamino": "Kamino — automated liquidity management on Solana.",
  "Uniswap V4": "Uniswap V4 — next-gen AMM with hooks and singleton architecture.",
  "Aerodrome": "Aerodrome — the central trading hub on Base.",
  "Velodrome": "Velodrome — the liquidity engine of Optimism.",
  "SushiSwap": "SushiSwap — multi-chain AMM with yield farming.",
  "Trader Joe": "Trader Joe — DEX on Avalanche with Liquidity Book.",
  "Camelot": "Camelot — Arbitrum-native DEX with custom pool types.",
};

function SortableHeader({ label, sortKey, sortBy, sortDir, onSort, colors }) {
  const [hovered, setHovered] = React.useState(false);
  const isActive = sortBy === sortKey;
  const isSortable = !!sortKey;

  return (
    <span
      onClick={isSortable ? () => onSort(sortKey) : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        color: isActive ? colors.textPrimary : (hovered && isSortable) ? colors.textSecondary : colors.labelLight,
        letterSpacing: 1.5,
        fontWeight: isActive ? 600 : 500,
        cursor: isSortable ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'color 0.15s ease',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {label}
      {isSortable && (
        <span style={{
          opacity: isActive ? 1 : hovered ? 0.5 : 0,
          transition: 'opacity 0.15s ease',
          color: isActive ? colors.textPrimary : colors.labelLight,
          display: 'inline-flex',
          alignItems: 'center',
          lineHeight: 1,
        }}>
          {isActive ? (
            sortDir === 'asc'
              ? <HiOutlineChevronUp size={10} />
              : <HiOutlineChevronDown size={10} />
          ) : (
            <HiOutlineChevronDown size={10} />
          )}
        </span>
      )}
      {HEADER_TOOLTIPS[label] && (
        <span onClick={e => e.stopPropagation()}>
          <InfoIcon text={HEADER_TOOLTIPS[label]} position="bottom" colors={colors} />
        </span>
      )}
    </span>
  );
}

function MobilePoolCard({ pool, isSelected, onSelect, colors, index }) {
  const protocolColor = PROTOCOL_COLORS[pool.protocol] || colors.textPrimary;
  const chainColor = CHAIN_COLORS[pool.chain] || colors.textSecondary;
  const risk = RISK_MAP[pool.risk] || { color: '#888', bg: '#f0f0f0', border: '#ccc' };
  const { ref, visible } = useScrollReveal();

  return (
    <div
      ref={ref}
      onClick={() => onSelect(isSelected ? null : pool)}
      style={{
        background: colors.surface,
        borderRadius: 12,
        padding: 16,
        border: isSelected ? `2px solid ${protocolColor}` : `1px solid ${colors.border}`,
        cursor: "pointer",
        transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateX(0)" : "translateX(12px)",
        transitionDelay: `${Math.min((index % 10) * 35, 250)}ms`,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <TokenStack tokens={pool.tokens} size={28} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 15,
            color: colors.textPrimary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
            {pool.name}
            {pool.poolId && (
              <a
                href={`https://defillama.com/yields/pool/${pool.poolId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-tooltip-id="pool-table-tooltip"
                data-tooltip-content="View on DeFi Llama"
                style={{
                  fontSize: 12,
                  opacity: 0.4,
                  transition: 'opacity 0.15s ease',
                  textDecoration: 'none',
                  lineHeight: 1,
                  flexShrink: 0,
                  color: colors.textSecondary,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; }}
              >
                <HiOutlineExternalLink size={12} />
              </a>
            )}
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" }}>
            {pool.stablecoin && (
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                color: colors.green,
                background: colors.greenBg,
                border: `1px solid ${colors.greenBorder}`,
                padding: "1px 6px",
                borderRadius: 3,
                fontWeight: 500,
              }}>
                STABLE
              </span>
            )}
            <span
              data-tooltip-id="pool-table-tooltip"
              data-tooltip-content={PROTOCOL_TOOLTIP_TEXT[pool.protocol] || pool.protocol}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                color: protocolColor,
                background: `${protocolColor}12`,
                border: `1px solid ${protocolColor}30`,
                padding: "1px 6px",
                borderRadius: 3,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <ProtocolIcon protocol={pool.protocol} size={10} />
              {pool.protocol}
            </span>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: chainColor,
              background: `${chainColor}12`,
              border: `1px solid ${chainColor}30`,
              padding: "1px 6px",
              borderRadius: 3,
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
            }}>
              <ChainIcon chain={pool.chain} size={10} />
              {pool.chain}
            </span>
          </div>
        </div>
      </div>

      {/* APR */}
      <div
        data-tooltip-id="pool-table-tooltip"
        data-tooltip-content={`Base APR: ${(pool.apyBase || 0).toFixed(2)}% + Reward APR: ${(pool.apyReward || 0).toFixed(2)}%`}
        style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: 28,
          color: (pool.apr || 0) > 100 ? '#FF4500' : (pool.apr || 0) > 20 ? colors.gold : colors.green,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          ...((pool.apr || 0) > 100 ? {
            textShadow: '0 0 8px rgba(255,69,0,0.4)',
          } : {}),
        }}
      >
        {(pool.apr || 0).toFixed(2)}%
        {(pool.apr || 0) > 100 && (
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            fontWeight: 700,
            color: '#FF4500',
            background: 'rgba(255,69,0,0.12)',
            border: '1px solid rgba(255,69,0,0.3)',
            padding: '2px 6px',
            borderRadius: 3,
            letterSpacing: 0.5,
            animation: 'hotPulse 2s ease-in-out infinite',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
          }}>
            <HiOutlineFire size={10} />
            HOT
          </span>
        )}
      </div>

      {/* Metrics grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {[
          { label: "TVL", value: fmt(pool.tvl), exact: `$${(pool.tvl || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}` },
          { label: "VOL 24H", value: fmt(pool.volume24h), exact: `$${(pool.volume24h || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}` },
          { label: "FEES 24H", value: fmt(pool.fees24h), exact: `$${(pool.fees24h || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}` },
        ].map(m => (
          <div key={m.label}
            data-tooltip-id="pool-table-tooltip"
            data-tooltip-content={m.exact}
          >
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelLight,
              letterSpacing: 1,
            }}>
              {m.label}
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              color: colors.textSecondary,
              fontWeight: 500,
            }}>
              {m.value}
            </div>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
          <span
            data-tooltip-id="pool-table-tooltip"
            data-tooltip-content={RISK_TOOLTIP_TEXT[pool.risk] || pool.risk}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              fontWeight: 600,
              color: risk.color,
              background: risk.bg,
              border: `1px solid ${risk.border}`,
              padding: "2px 8px",
              borderRadius: 3,
            }}
          >
            {pool.risk}
          </span>
        </div>
      </div>
    </div>
  );
}

function PoolRow({ index, onClick, style, isSelected, colors, children }) {
  const { ref, visible } = useScrollReveal();

  return (
    <div
      ref={ref}
      onClick={onClick}
      style={{
        ...style,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateX(0)" : "translateX(10px)",
        transition: "opacity 0.45s ease, transform 0.45s ease, background 0.1s",
        transitionDelay: `${Math.min((index % 15) * 25, 250)}ms`,
      }}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = colors.hover; }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
    >
      {children}
    </div>
  );
}

const rowVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

export default function PoolTable({ pools, selectedId, onSelect, sortBy, sortDir, onSort }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Track whether the component has completed its initial render
  const hasInitiallyRendered = useRef(false);
  useEffect(() => {
    // Mark as rendered after first paint
    const id = requestAnimationFrame(() => { hasInitiallyRendered.current = true; });
    return () => cancelAnimationFrame(id);
  }, []);

  // Generate a key that changes when sort/filter changes to trigger AnimatePresence
  const poolOrderKey = useMemo(
    () => pools.map(p => p.id).join(','),
    [pools]
  );

  // Track the previous order key to know when rows change
  const prevOrderKeyRef = useRef(poolOrderKey);
  const shouldAnimate = hasInitiallyRendered.current && prevOrderKeyRef.current !== poolOrderKey;
  useEffect(() => {
    prevOrderKeyRef.current = poolOrderKey;
  }, [poolOrderKey]);

  const tooltipStyle = {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 11,
    lineHeight: 1.5,
    borderRadius: 8,
    padding: '8px 12px',
    maxWidth: 280,
    zIndex: 10000,
  };

  if (isMobile) {
    return (
      <div style={{ padding: "0 12px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
        {pools.map((pool, i) => (
          <MobilePoolCard
            key={pool.id}
            pool={pool}
            isSelected={pool.id === selectedId}
            onSelect={onSelect}
            colors={colors}
            index={i}
          />
        ))}
        <ReactTooltip
          id="pool-table-tooltip"
          place="top"
          style={tooltipStyle}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: "0 24px 24px" }}>
      <div style={{
        background: colors.surface,
        borderRadius: 12,
        boxShadow: `0 1px 4px ${colors.shadow}`,
        overflow: isTablet ? "auto" : "hidden",
        transition: "background-color 0.3s ease",
        ...(isTablet ? { WebkitOverflowScrolling: "touch" } : {}),
      }}>
        {/* Header */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "2.5fr 1fr 1fr 1fr 1fr 80px",
          background: colors.surfaceAlt,
          borderBottom: `1px solid ${colors.borderLight}`,
          padding: "12px 20px",
          ...(isTablet ? { minWidth: 800 } : {}),
        }}>
          {["POOL", "APR", "TVL", "VOL 24H", "FEES 24H", "RISK"].map(h => (
            <SortableHeader
              key={h}
              label={h}
              sortKey={HEADER_SORT_MAP[h] || null}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              colors={colors}
            />
          ))}
        </div>

        {/* Rows */}
        <AnimatePresence mode="popLayout" initial={false}>
          {pools.map((pool, i) => {
            const isSelected = pool.id === selectedId;
            const protocolColor = PROTOCOL_COLORS[pool.protocol] || colors.textPrimary;
            const chainColor = CHAIN_COLORS[pool.chain] || colors.textSecondary;
            const risk = RISK_MAP[pool.risk] || { color: '#888', bg: '#f0f0f0', border: '#ccc' };
            const isHotApr = (pool.apr || 0) > 100;

            return (
              <motion.div
                key={pool.id}
                layout={shouldAnimate ? true : false}
                variants={shouldAnimate ? rowVariants : undefined}
                initial={shouldAnimate ? "initial" : false}
                animate="animate"
                exit={shouldAnimate ? "exit" : undefined}
                transition={{
                  duration: 0.25,
                  delay: shouldAnimate ? Math.min(i * 0.02, 0.3) : 0,
                  ease: "easeOut",
                  layout: { duration: 0.25, ease: "easeInOut" },
                }}
              >
                <PoolRow
                  index={i}
                  onClick={() => onSelect(isSelected ? null : pool)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2.5fr 1fr 1fr 1fr 1fr 80px",
                    padding: "13px 20px",
                    borderBottom: `1px solid ${colors.borderSubtle}`,
                    cursor: "pointer",
                    background: isSelected ? colors.selectedRowBg
                      : isHotApr ? 'rgba(255,69,0,0.04)' : "transparent",
                    borderLeft: isSelected ? `3px solid ${protocolColor}`
                      : isHotApr ? '3px solid rgba(255,69,0,0.5)' : "3px solid transparent",
                    alignItems: "center",
                    ...(isTablet ? { minWidth: 800 } : {}),
                  }}
                  isSelected={isSelected}
                  colors={colors}
                >
                  {/* POOL */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <TokenStack tokens={pool.tokens} size={26} />
                    <span style={{
                      fontFamily: "'DM Serif Display', serif",
                      fontSize: 15,
                      color: colors.textPrimary,
                    }}>
                      {pool.name}
                    </span>
                    {pool.stablecoin && (
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 9,
                        color: colors.green,
                        background: colors.greenBg,
                        border: `1px solid ${colors.greenBorder}`,
                        padding: "1px 6px",
                        borderRadius: 3,
                        fontWeight: 500,
                      }}>
                        STABLE
                      </span>
                    )}
                    <span
                      data-tooltip-id="pool-table-tooltip"
                      data-tooltip-content={PROTOCOL_TOOLTIP_TEXT[pool.protocol] || pool.protocol}
                      style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 9,
                        color: protocolColor,
                        background: `${protocolColor}12`,
                        border: `1px solid ${protocolColor}30`,
                        padding: "1px 6px",
                        borderRadius: 3,
                        fontWeight: 500,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                      }}
                    >
                      <ProtocolIcon protocol={pool.protocol} size={10} />
                      {pool.protocol}
                    </span>
                    <span style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 9,
                      color: chainColor,
                      background: `${chainColor}12`,
                      border: `1px solid ${chainColor}30`,
                      padding: "1px 6px",
                      borderRadius: 3,
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                    }}>
                      <ChainIcon chain={pool.chain} size={10} />
                      {pool.chain}
                    </span>
                    {pool.poolId && (
                      <a
                        href={`https://defillama.com/yields/pool/${pool.poolId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        data-tooltip-id="pool-table-tooltip"
                        data-tooltip-content="View on DeFi Llama"
                        style={{
                          fontSize: 12,
                          marginLeft: 6,
                          opacity: 0.4,
                          transition: 'opacity 0.15s ease',
                          textDecoration: 'none',
                          lineHeight: 1,
                          color: colors.textSecondary,
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; }}
                      >
                        <HiOutlineExternalLink size={12} />
                      </a>
                    )}
                  </div>

                  {/* APR */}
                  <span
                    data-tooltip-id="pool-table-tooltip"
                    data-tooltip-content={`Base APR: ${(pool.apyBase || 0).toFixed(2)}% + Reward APR: ${(pool.apyReward || 0).toFixed(2)}%`}
                    style={{
                      fontFamily: "'DM Serif Display', serif",
                      fontSize: 20,
                      color: (pool.apr || 0) > 100 ? '#FF4500' : (pool.apr || 0) > 20 ? colors.gold : colors.green,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      ...((pool.apr || 0) > 100 ? {
                        textShadow: '0 0 8px rgba(255,69,0,0.4)',
                      } : {}),
                    }}
                  >
                    {(pool.apr || 0).toFixed(2)}%
                    {(pool.apr || 0) > 100 && (
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 8,
                        fontWeight: 700,
                        color: '#FF4500',
                        background: 'rgba(255,69,0,0.12)',
                        border: '1px solid rgba(255,69,0,0.3)',
                        padding: '1px 5px',
                        borderRadius: 3,
                        letterSpacing: 0.5,
                        animation: 'hotPulse 2s ease-in-out infinite',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                      }}>
                        <HiOutlineFire size={9} />
                        HOT
                      </span>
                    )}
                  </span>

                  {/* TVL */}
                  <span
                    data-tooltip-id="pool-table-tooltip"
                    data-tooltip-content={`$${(pool.tvl || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 12,
                      color: colors.textSecondary,
                    }}
                  >
                    {fmt(pool.tvl)}
                  </span>

                  {/* VOL 24H */}
                  <span
                    data-tooltip-id="pool-table-tooltip"
                    data-tooltip-content={`$${(pool.volume24h || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 12,
                      color: colors.textSecondary,
                    }}
                  >
                    {fmt(pool.volume24h)}
                  </span>

                  {/* FEES 24H */}
                  <span
                    data-tooltip-id="pool-table-tooltip"
                    data-tooltip-content={`$${(pool.fees24h || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 12,
                      color: colors.textSecondary,
                    }}
                  >
                    {fmt(pool.fees24h)}
                  </span>

                  {/* RISK */}
                  <span
                    data-tooltip-id="pool-table-tooltip"
                    data-tooltip-content={RISK_TOOLTIP_TEXT[pool.risk] || pool.risk}
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 9,
                      fontWeight: 600,
                      color: risk.color,
                      background: risk.bg,
                      border: `1px solid ${risk.border}`,
                      padding: "2px 8px",
                      borderRadius: 3,
                      textAlign: "center",
                      width: "fit-content",
                    }}
                  >
                    {pool.risk}
                  </span>
                </PoolRow>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
      <ReactTooltip
        id="pool-table-tooltip"
        place="top"
        style={tooltipStyle}
        delayShow={200}
      />
    </div>
  );
}
